type RGBAColor = [number, number, number, number?]
type RGBA = { r: number; g: number; b: number; a: number }

export const color = {
  blue: '#4285F4',
  red: '#EC2C62',
  yellow: '#E79725',
}

export const rgbaStrToObj = (input: string): RGBA => {
  if (!input) {
    return { r: 255, g: 255, b: 255, a: 0 }
  }
  const match = input.match(/\d+/g)
  if (!match) {
    throw new Error('Color ' + input + ' did not match pattern rgb[a](r, g, b[, a]).')
  }

  // Match from an rgba string comes back as [r, g, b, 0 ,a]
  // eg. 'rgba(145, 25, 85, 0.78)' -> [145, 25, 85, 0, 78]
  // eg. 'rgba(145, 25, 85, 0.7)' -> [145, 25, 85, 0, 7]

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [r, g, b, _, a] = match.map(Number)
  // Covert alpha to percentage
  const alpha = isNaN(a) ? 1 : a > 10 ? a / 100 : a / 10
  return { r, g, b, a: alpha }
}

// convertgs rgba string to GPU based 4-channel color with the A from 0-255
export const rgbaStrToArray = (input: string, overrideAlpha?: number): RGBAColor => {
  const obj = rgbaStrToObj(input)
  return [obj.r, obj.g, obj.b, overrideAlpha ?? obj.a * 255]
}

export const rgbaObjToStr = (input: RGBA): string => {
  return `rgba(${input.r}, ${input.g}, ${input.b}, ${input.a})`
}

export const rgbaColorToStr = (input: RGBAColor): string => {
  return `rgba(${input[0]}, ${input[1]}, ${input[2]}, ${input[3] ? input[3] / 255 : 255})`
}

export const rgbaObjOpacity = (input: RGBA, opacity: number): RGBA => {
  return { ...input, a: opacity }
}

export const rgbaStrOpacity = (input: string, opacity: number): string => {
  return rgbaObjToStr(rgbaObjOpacity(rgbaStrToObj(input), opacity))
}

export function convertRGBtoHex(r: number | string, g: number | string, b: number | string) {
  r = r.toString(16)
  g = g.toString(16)
  b = b.toString(16)

  if (r.length == 1) {
    r = '0' + r
  }
  if (g.length == 1) {
    g = '0' + g
  }
  if (b.length == 1) {
    b = '0' + b
  }

  return '#' + r + g + b
}

export function convertStringRGBtoHex(input: string, fallbackColor?: string) {
  const match = input.match(/\d+/g)
  if (!match) {
    if (fallbackColor) {
      return fallbackColor
    }
    throw new Error('Color ' + input + ' did not match pattern rgb[a](r, g, b[, a]).')
  }

  const [r, g, b] = match.map(Number)
  return convertRGBtoHex(r, g, b)
}

export function rgbaArrayToString(color: RGBAColor): string {
  const [r, g, b, a] = color
  const alpha = a !== undefined ? a / 255 : 1
  return `rgba(${r},${g},${b},${alpha})`
}

// Function to convert RGBA to equivalent RGB by blending with a white background
export function rgbaToRgb(color: string) {
  const rgba = rgbaStrToArray(color)
  const alpha = (rgba[3] || 255) / 255
  const background = [255, 255, 255]

  return rgbaArrayToString([
    Math.floor((1 - alpha) * background[0] + alpha * rgba[0] + 0.5),
    Math.floor((1 - alpha) * background[1] + alpha * rgba[1] + 0.5),
    Math.floor((1 - alpha) * background[2] + alpha * rgba[2] + 0.5),
  ])
}

export function hexToRGBA(hex: string, alpha: number = 1): string {
  let r: number, g: number, b: number

  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, '')

  // Parse r, g, b values based on the length of the hex string
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16)
    g = parseInt(hex[1] + hex[1], 16)
    b = parseInt(hex[2] + hex[2], 16)
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16)
    g = parseInt(hex.slice(2, 4), 16)
    b = parseInt(hex.slice(4, 6), 16)
  } else {
    throw new Error('Invalid hex color format')
  }

  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
